// ─────────────────────────────────────────────────────────────────────────
// useInAppBrowser.ts
// Detects when the page is running inside a chat app's in-app browser
// (LINE, Facebook, Instagram) instead of a real Safari/Chrome tab.
//
// Why this matters: several in-app webviews (most notably LINE's, and to a
// lesser extent Facebook/Instagram's) either don't grant getUserMedia()
// camera access at all, or silently fail it. When that happens,
// useDeliveryCamera.ts falls back to <input type="file" capture="...">,
// which — inside these webviews — often shows the full "attach a file"
// picker (with gallery access) instead of jumping straight to the camera,
// which looks to the driver like "the app is just asking me to attach a
// photo" instead of taking one live.
//
// LINE has an official trick for this: appending `?openExternalBrowser=1`
// (or `&openExternalBrowser=1`) to the URL and reloading makes LINE's
// in-app browser hand the page off to the device's real default browser
// (Chrome on Android, Safari on iOS), where camera access works normally.
// Facebook/Instagram have no equivalent public parameter, so for those we
// can only show the manual "tap ⋯ → Open in Browser" instructions.
// ─────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';

export type InAppBrowserKind = 'line' | 'facebook' | 'instagram' | null;

export interface UseInAppBrowserResult {
  /** Which in-app browser was detected, or null if this looks like a normal browser. */
  kind: InAppBrowserKind;
  isInAppBrowser: boolean;
  /**
   * Attempts to force the current in-app browser to reopen this exact page
   * in the device's real external browser. Only works for LINE (returns
   * true and navigates away). Returns false for every other case (Facebook,
   * Instagram, or no in-app browser detected) since no such trick exists —
   * the caller should fall back to showing manual instructions instead.
   */
  tryOpenExternal: () => boolean;
}

function detectInAppBrowserKind(userAgent: string): InAppBrowserKind {
  // LINE's webview UA contains "Line/x.y.z" (capital L).
  if (/\bLine\//i.test(userAgent)) return 'line';
  // Facebook's in-app browser UA contains "FBAN" (native app) or "FBAV" (app version).
  if (/\bFBAN\b|\bFBAV\b/i.test(userAgent)) return 'facebook';
  // Instagram's in-app browser UA contains "Instagram".
  if (/\bInstagram\b/i.test(userAgent)) return 'instagram';
  return null;
}

export function useInAppBrowser(): UseInAppBrowserResult {
  const kind = useMemo<InAppBrowserKind>(() => {
    if (typeof navigator === 'undefined') return null;
    return detectInAppBrowserKind(navigator.userAgent || '');
  }, []);

  const tryOpenExternal = (): boolean => {
    if (kind !== 'line') return false;
    if (typeof window === 'undefined') return false;
    const url = new URL(window.location.href);
    url.searchParams.set('openExternalBrowser', '1');
    window.location.href = url.toString();
    return true;
  };

  return { kind, isInAppBrowser: kind !== null, tryOpenExternal };
}
